import React, { useState, useEffect } from 'react';
import { TrashIcon, LinkIcon } from '@heroicons/react/24/solid';
import { ragApi } from '../api/apiRag';

export default function RagURLs() {
  const [urls, setUrls] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [loading, setLoading] = useState(false); // Loading state for adding a URL
  const [loadingDelete, setLoadingDelete] = useState(false); // Loading state for delete
  const [showDialog, setShowDialog] = useState(false);
  const [urlToDelete, setUrlToDelete] = useState(null);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');

  useEffect(() => {
    const fetchUrls = async () => {
      try {
        const response = await ragApi.get('/blogs');
        if (response) {
          try {
            setUrls(response);
          } catch (jsonError) {
            console.error("Failed to parse JSON from /blogs:", jsonError);
            setDialogMessage('Error: The server returned an invalid response.');
            setShowResultDialog(true);
          }
        } else {
          console.error("Failed to fetch blogs, status:", response.status);
          setUrls([]);
        }
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };

    fetchUrls();
  }, []);

  const handleDelete = async (urlId) => {
    setLoadingDelete(true); 
    
    const resApi = await ragApi.post('/vectorDelete', { id: urlId, type: 'blog' });

    setShowDialog(false);
    setLoadingDelete(false); 

    if (resApi && resApi.status === 'success') {
      setUrls(urls.filter((url) => url.id !== urlId));
      setDialogMessage('Item deleted successfully!');
      setShowResultDialog(true);
      return;
    }
    setDialogMessage('Error in vector store deletion.');
    setShowResultDialog(true);
  };

  const openDialog = (id) => {
    setUrlToDelete(id);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
  };

  const handleAddUrl = async () => {
    if (newUrl.trim() === '') {
      setDialogMessage('Please enter a valid URL.');
      setShowResultDialog(true);
      return;
    }

    const urlExists = urls.some((url) => url.url === newUrl);
    if (urlExists) {
      setDialogMessage('This URL is already in the list.');
      setShowResultDialog(true);
      return;
    }

    setLoading(true); // Show loading spinner
    
    const resApi = await ragApi.post('/vectorSave', { url: newUrl });

    if (resApi && resApi.status === 'success') {
      const updatedUrls = await ragApi.get('/blogs');
      setUrls(updatedUrls);
      setNewUrl('');
      setDialogMessage('New URL added successfully!');
    } else {
      setDialogMessage('Error in vector store insertion.');
    }

    setShowResultDialog(true);

    setLoading(false);
  };

  const closeResultDialog = () => {
    setShowResultDialog(false);
  };

  return (
    <div className="p-4 pt-0 mt-4" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
      {/* Add New URL Form */}
      <div className="mb-6">
        <input
          value={newUrl}
          onChange={(e) => setNewUrl(e.target.value)}
          placeholder="Enter a new URL"
          className="w-full p-2 border rounded-lg mb-2"
        />
        <button
          onClick={handleAddUrl}
          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
          disabled={loading}
        >
          Add New URL
          {loading && (
            <svg
              className="animate-spin ml-2 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              ></path>
            </svg>
          )}
        </button>
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-12 bg-gray-200 p-2 font-bold sticky top-0">
        <div className="col-span-1">#</div>
        <div className="col-span-10">URL</div>
        <div className="col-span-1">Actions</div>
      </div>

      {/* URL Rows */}
      {urls.map((url, index) => (
        <div key={url.id} className="grid grid-cols-12 items-center bg-gray-100 p-2 mb-2 rounded-md shadow-sm">
          <div className="col-span-1">{index + 1}</div>
          <div className="col-span-10 flex items-center">
            <LinkIcon className="h-5 w-5 text-blue-500 mr-2" />
            <a href={url.url} target="_blank" rel="noopener noreferrer" className="text-black hover:underline break-all">
              {url.url}
            </a>
          </div>
          <div className="col-span-1 flex justify-center">
            <TrashIcon
              className="h-5 w-5 text-gray-500 cursor-pointer hover:text-red-500"
              onClick={() => openDialog(url.id)}
            />
          </div>
        </div>
      ))}

      {/* Delete Confirmation Dialog */}
      {showDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
          <div className="bg-white p-6 rounded-md shadow-lg">
            <p>Are you sure you want to delete this URL?</p>
            <div className="mt-4 flex justify-end">
              <button className="mr-2 px-4 py-2 bg-gray-300 rounded-md" onClick={closeDialog}>
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-red-500 text-white rounded-md flex items-center"
                onClick={() => handleDelete(urlToDelete)}
                disabled={loadingDelete} // Disable delete button while loading
              >
                Confirm
                {loadingDelete && (
                  <svg
                    className="animate-spin ml-2 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    ></path>
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result Dialog */}
      {showResultDialog && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50">
          <div className="bg-white p-6 rounded-md shadow-lg">
            <p>{dialogMessage}</p>
            <div className="mt-4 flex justify-end">
              <button className="px-4 py-2 bg-blue-500 text-white rounded-md" onClick={closeResultDialog}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
